import * as React from 'react';
import { CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { MarketingPageHead } from '../MarketingPageHead';

type CheckStatus = 'ok' | 'fail';
interface Check { name: string; status: CheckStatus; detail: string | null }
interface DetailedHealth {
  status: 'operational' | 'degraded';
  timestamp: string;
  checks: Check[];
}

const POLL_MS = 30_000;

function pillClass(status: 'operational' | 'degraded' | 'outage'): string {
  if (status === 'operational') return 'bg-lime-neon text-ink';
  if (status === 'degraded')    return 'bg-coral text-white';
  return 'bg-rose-500 text-white';
}

function pillLabel(status: 'operational' | 'degraded' | 'outage'): string {
  if (status === 'operational') return 'All systems operational';
  if (status === 'degraded')    return 'Degraded performance';
  return 'Outage';
}

export function StatusPage() {
  const [health, setHealth] = React.useState<DetailedHealth | null>(null);
  const [outage, setOutage] = React.useState(false);
  const [lastChecked, setLastChecked] = React.useState<Date | null>(null);

  const fetchHealth = React.useCallback(async () => {
    try {
      const res = await fetch('/api/health-detailed', { cache: 'no-store' });
      if (!res.ok) { setOutage(true); setLastChecked(new Date()); return; }
      const body: DetailedHealth = await res.json();
      setHealth(body);
      setOutage(false);
      setLastChecked(new Date());
    } catch {
      setOutage(true);
      setLastChecked(new Date());
    }
  }, []);

  React.useEffect(() => {
    void fetchHealth();
    const id = setInterval(fetchHealth, POLL_MS);
    return () => clearInterval(id);
  }, [fetchHealth]);

  const overall: 'operational' | 'degraded' | 'outage' = outage
    ? 'outage'
    : health?.status === 'degraded'
      ? 'degraded'
      : 'operational';

  return (
    <section className="max-w-4xl mx-auto px-6 lg:px-8 py-20">
      <MarketingPageHead
        title="System status"
        description="Live operational status for the Worqly platform — API, dashboard, payments, and email."
      />
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-coral mb-3">System status</p>
      <h1 className="font-light text-hero-lg text-ink dark:text-white leading-tight tracking-[-0.03em] mb-6">Worqly status</h1>

      <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-bold uppercase tracking-[0.10em] ${pillClass(overall)}`}>
        <span className="h-2 w-2 rounded-full bg-current animate-pulse-soft" />
        {pillLabel(overall)}
      </div>

      <p className="mt-3 text-[13px] text-ink/55 dark:text-white/55">
        {lastChecked ? `Last checked ${formatAgo(lastChecked)}` : 'Checking…'}
      </p>

      <div className="mt-10 space-y-3">
        {(health?.checks ?? []).map((c) => (
          <div key={c.name} className="flex items-center justify-between rounded-2xl bg-white/72 dark:bg-white/[0.04] backdrop-blur-md shadow-pill dark:shadow-none ring-1 ring-ink/[0.08] dark:ring-white/10 px-5 py-4">
            <div className="flex items-center gap-3">
              {c.status === 'ok'
                ? <CheckCircle2 className="h-4 w-4 text-coral-600" strokeWidth={2.5} />
                : <XCircle className="h-4 w-4 text-rose-600" strokeWidth={2.5} />
              }
              <span className="text-[14px] font-semibold text-ink dark:text-white capitalize">{c.name}</span>
              {c.detail && <span className="text-[12px] text-ink/55 dark:text-white/55 hidden md:inline">· {c.detail}</span>}
            </div>
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.10em] px-2.5 py-1 rounded-full ${c.status === 'ok' ? 'bg-lime-neon text-ink' : 'bg-rose-500/15 text-rose-700'}`}>
              {c.status === 'ok' ? 'Operational' : 'Failing'}
            </span>
          </div>
        ))}
        {outage && (
          <div className="rounded-2xl bg-rose-500/10 ring-1 ring-rose-500/20 px-5 py-4 text-[13px] text-rose-700 flex items-center gap-3">
            <AlertCircle className="h-4 w-4" />
            <span>We can't currently reach the Worqly API from this browser. Status will refresh automatically.</span>
          </div>
        )}
      </div>

      <p className="mt-12 text-[12px] text-ink/55 dark:text-white/55">
        Automated checks run every 30 seconds. Incident history is tracked in our operations runbook.
      </p>
    </section>
  );
}

function formatAgo(d: Date): string {
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  return `${Math.floor(s / 60)}m ago`;
}
